import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  changeEmployeeStatus,
  createEmployeeNote,
  createReport,
  fetchAdvances,
  fetchAttendance,
  fetchDepartments,
  fetchEmployee,
  fetchEmployeeActivity,
  fetchEmployeeNotes,
  fetchOvertime,
  fetchReports,
  saveSalary,
  submitReport,
  transferEmployee,
} from "../../api/HrAPIs";
import { Empty, Field, HrFrame, HrGuard, StatusPill, day, inputClass, labelOf, money, useHr } from "./hrUi";

const TABS = [
  ["overview", "نظرة عامة"],
  ["activity", "النشاط"],
  ["performance", "الأداء"],
  ["reports", "التقارير"],
  ["notes", "ملاحظات"],
  ["attendance", "الحضور"],
  ["payroll", "الراتب"],
  ["advances", "السلف"],
  ["overtime", "الإضافي"],
];

export default function EmployeeProfilePage() {
  return (
    <HrGuard anyOf={["HR.ViewEmployees", "HR.ManageEmployees", "HR.ViewDepartmentEmployees"]}>
      <ProfileBody />
    </HrGuard>
  );
}

function ProfileBody() {
  const { id } = useParams();
  const hr = useHr();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("overview");
  const employee = useQuery({ queryKey: ["hrEmployee", id], queryFn: () => fetchEmployee(id) });
  const data = employee.data;

  return (
    <HrFrame title={data?.fullName || "ملف الموظف"} subtitle={data ? `${data.employeeCode} · حساب رقم ${data.userId}` : ""}>
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)} className={`shrink-0 rounded-full px-3 py-1.5 text-sm ${tab === key ? "bg-indigo-600 text-white" : "bg-white text-slate-600"}`}>
            {label}
          </button>
        ))}
      </div>
      {employee.isLoading && <p className="text-sm text-slate-500">جار التحميل…</p>}
      {employee.error && <p className="text-sm text-rose-600">{employee.error.message}</p>}
      {data && tab === "overview" && <Overview employee={data} canManage={hr.can("HR.ManageEmployees")} onChanged={() => queryClient.invalidateQueries({ queryKey: ["hrEmployee", id] })} />}
      {data && tab === "activity" && <Activity employeeId={id} />}
      {data && tab === "performance" && <Activity employeeId={id} />}
      {data && tab === "reports" && <Reports employee={data} canCreate={hr.can("HR.CreateReports")} />}
      {data && tab === "notes" && <Notes employeeId={id} canWrite={hr.can("HR.ManageNotes")} />}
      {data && tab === "attendance" && <Attendance employeeId={id} />}
      {data && tab === "payroll" && <Payroll employee={data} canEdit={hr.can("HR.ManagePayroll")} />}
      {data && tab === "advances" && <SimpleList queryKey={["hrAdvances", id]} queryFn={() => fetchAdvances({ employeeId: id })} render={(row) => `${money(row.requestedAmount)} · المتبقي ${money(row.remainingBalance)}`} />}
      {data && tab === "overtime" && <SimpleList queryKey={["hrOvertime", id]} queryFn={() => fetchOvertime({ employeeId: id })} render={(row) => `${day(row.overtimeDate)} · فعلي ${row.actualDurationValue} ${row.actualDurationUnit} · تعويض ${money(row.totalAmount)}`} />}
    </HrFrame>
  );
}

function Overview({ employee, canManage, onChanged }) {
  const [reason, setReason] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const departments = useQuery({ queryKey: ["hrDepartments"], queryFn: fetchDepartments, enabled: canManage });
  const act = async (work) => {
    try { await work(); toast.success("تم الحفظ"); onChanged(); }
    catch (error) { toast.error(error.message); }
  };
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-3">
          {employee.profilePictureUrl && <img src={employee.profilePictureUrl} alt="" className="h-14 w-14 rounded-full object-cover" />}
          <StatusPill value={employee.employmentStatus} />
        </div>
        <Info label="الإيميل" value={employee.workEmail} />
        <Info label="الهاتف" value={employee.phoneNumber} />
        <Info label="التعيين" value={day(employee.hiringDate)} />
        <Info label="القسم" value={employee.departmentName} />
        <Info label="المسمى" value={employee.jobTitle} />
        <Info label="المدير" value={employee.managerName} />
        <Info label="نوع التوظيف" value={employee.employmentType} />
        {employee.administrativeNotes && <Info label="ملاحظات إدارية" value={employee.administrativeNotes} />}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="mb-2 font-bold">سجل الأقسام</h3>
        {(employee.departmentHistory || []).length === 0 && <Empty text="لا يوجد نقل سابق." />}
        {(employee.departmentHistory || []).map((row) => (
          <p key={row.id} className="border-b border-slate-100 py-2 text-sm">{row.departmentName} · {day(row.startedOn)} → {row.endedOn ? day(row.endedOn) : "الآن"} · {row.reason}</p>
        ))}
        {canManage && (
          <div className="mt-4 grid gap-2">
            <Field label="نقل إلى قسم">
              <select className={inputClass} value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
                <option value="">اختر القسم</option>
                {(departments.data || []).filter((row) => row.isActive !== false && row.id !== employee.departmentId).map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </select>
            </Field>
            <Field label="السبب"><input className={inputClass} value={reason} onChange={(event) => setReason(event.target.value)} /></Field>
            <button type="button" className="rounded-xl bg-slate-900 px-3 py-2 text-sm text-white" onClick={() => {
              if (!departmentId) { toast.error("اختر القسم"); return; }
              act(() => transferEmployee(employee.id, { departmentId: Number(departmentId), reason }));
            }}>نقل</button>
            <div className="flex gap-2">
              {["Active", "Suspended", "Resigned"].map((status) => (
                <button key={status} type="button" className="rounded-xl border px-3 py-2 text-xs" onClick={() => act(() => changeEmployeeStatus(employee.id, { status, reason: reason || "تحديث الحالة" }))}>{labelOf(status)}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return <p className="py-1 text-sm"><span className="text-slate-500">{label}: </span>{value || "—"}</p>;
}

function Activity({ employeeId }) {
  const now = new Date();
  const query = useQuery({
    queryKey: ["hrActivity", employeeId],
    queryFn: () => fetchEmployeeActivity(employeeId, { from: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01` }),
  });
  const tickets = query.data?.tickets;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {query.error && <p className="text-sm text-rose-600">{query.error.message}</p>}
      {tickets && [
        ["أُنشئت", tickets.created],
        ["مُسندة", tickets.assigned],
        ["تم التعامل معها", tickets.handled],
        ["حُلّت", tickets.resolved],
        ["أُغلقت", tickets.closed],
        ["أُعيد فتحها", tickets.reopened],
        ["معلقة الآن", tickets.pending],
        ["متوسط الحل بالساعات", tickets.averageResolutionHours ?? "—"],
      ].map(([label, value]) => (
        <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      ))}
      <div className="md:col-span-2">
        <h3 className="mb-2 font-bold">مؤشرات مسجلة</h3>
        {(query.data?.kpis || []).length === 0 && <Empty text="لا توجد قيم مؤشرات لهذا الشهر. مؤشرات الإنتاج تُسجل يدوياً لأن الإنتاج غير مربوط بموظف." />}
        {(query.data?.kpis || []).map((kpi) => <p key={kpi.id} className="text-sm">{kpi.name}: {kpi.value} {kpi.unit}</p>)}
      </div>
    </div>
  );
}

function Reports({ employee, canCreate }) {
  const queryClient = useQueryClient();
  const reports = useQuery({ queryKey: ["hrReports", employee.id], queryFn: () => fetchReports({ employeeId: employee.id }) });
  const [form, setForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1, rating: 8, achievements: "", strengths: "", weaknesses: "", areasForImprovement: "", managerNotes: "", improvementPlan: "", goalsForNextMonth: "" });
  const save = async (event) => {
    event.preventDefault();
    try {
      await createReport({ ...form, employeeId: employee.id, rating: Number(form.rating), year: Number(form.year), month: Number(form.month) });
      toast.success("تم حفظ المسودة");
      queryClient.invalidateQueries({ queryKey: ["hrReports", employee.id] });
    } catch (error) { toast.error(error.message); }
  };
  return (
    <div className="grid gap-4">
      {(reports.data || []).map((report) => (
        <div key={report.id} className="flex items-center justify-between rounded-2xl border bg-white p-3">
          <span>{report.month}/{report.year} · {report.rating}/10</span>
          <span className="flex items-center gap-2">
            <StatusPill value={report.status} />
            {report.status === "Draft" && canCreate && <button type="button" className="text-sm text-indigo-600" onClick={() => submitReport(report.id).then(() => queryClient.invalidateQueries({ queryKey: ["hrReports", employee.id] })).catch((error) => toast.error(error.message))}>إرسال</button>}
          </span>
        </div>
      ))}
      {canCreate && (
        <form onSubmit={save} className="grid gap-2 rounded-2xl border bg-white p-4 md:grid-cols-2">
          <Field label="السنة"><input className={inputClass} type="number" value={form.year} onChange={(event) => setForm({ ...form, year: event.target.value })} /></Field>
          <Field label="الشهر"><input className={inputClass} type="number" min="1" max="12" value={form.month} onChange={(event) => setForm({ ...form, month: event.target.value })} /></Field>
          <Field label="التقييم من 10"><input className={inputClass} type="number" min="1" max="10" value={form.rating} onChange={(event) => setForm({ ...form, rating: event.target.value })} /></Field>
          {[
            ["achievements", "الإنجازات"],
            ["strengths", "نقاط القوة"],
            ["weaknesses", "نقاط الضعف"],
            ["areasForImprovement", "مجالات التحسين"],
            ["managerNotes", "ملاحظات المدير"],
            ["improvementPlan", "خطة التحسين"],
            ["goalsForNextMonth", "أهداف الشهر القادم"],
          ].map(([key, label]) => (
            <Field key={key} label={label}>
              <textarea className={inputClass} value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })} />
            </Field>
          ))}
          <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white md:col-span-2">حفظ مسودة</button>
        </form>
      )}
    </div>
  );
}

function Notes({ employeeId, canWrite }) {
  const queryClient = useQueryClient();
  const notes = useQuery({ queryKey: ["hrNotes", employeeId], queryFn: () => fetchEmployeeNotes(employeeId) });
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  if (notes.error) return <p className="text-sm text-rose-600">{notes.error.message}</p>;
  return (
    <div className="grid gap-3">
      {(notes.data || []).map((note) => (
        <article key={note.id} className="rounded-2xl border bg-white p-4">
          <p className="text-sm">{note.content}</p>
          <p className="mt-2 text-xs text-slate-500">{note.createdBy} · {day(note.createdAtUtc)} · {note.category || "عام"}</p>
        </article>
      ))}
      {(notes.data || []).length === 0 && !notes.isLoading && <Empty text="لا توجد ملاحظات، أو ليست لديك صلاحية الملاحظات." />}
      {canWrite && (
        <form className="grid gap-2" onSubmit={async (event) => {
          event.preventDefault();
          try {
            await createEmployeeNote({ employeeId: Number(employeeId), content, category });
            setContent("");
            queryClient.invalidateQueries({ queryKey: ["hrNotes", employeeId] });
          } catch (error) { toast.error(error.message); }
        }}>
          <textarea className={inputClass} required value={content} onChange={(event) => setContent(event.target.value)} placeholder="ملاحظة داخلية" />
          <input className={inputClass} value={category} onChange={(event) => setCategory(event.target.value)} placeholder="تصنيف اختياري" />
          <button className="rounded-xl bg-slate-900 px-4 py-2 text-sm text-white">إضافة</button>
        </form>
      )}
    </div>
  );
}

function Attendance({ employeeId }) {
  const rows = useQuery({ queryKey: ["hrAttendance", employeeId], queryFn: () => fetchAttendance({ employeeId }) });
  if (rows.error) return <p className="text-sm text-rose-600">{rows.error.message}</p>;
  return (
    <div className="grid gap-2">
      {(rows.data || []).map((row) => (
        <div key={row.id} className="flex items-center justify-between rounded-2xl border bg-white p-3 text-sm">
          <span>{day(row.workDate)} · {row.source}</span>
          <StatusPill value={row.status} />
        </div>
      ))}
      {(rows.data || []).length === 0 && <Empty text="لا توجد سجلات حضور." />}
    </div>
  );
}

function Payroll({ employee, canEdit }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ salaryType: "Monthly", baseAmount: "", effectiveFrom: "", reason: "", componentName: "", componentKind: "Earning", componentAmount: "" });
  if (!employee.salary && !employee.salaryHistory) return <Empty text="بيانات الراتب مخفية. تحتاج صلاحية رواتب منفصلة عن إدارة الموظفين." />;
  const save = async (event) => {
    event.preventDefault();
    try {
      await saveSalary({
        employeeId: employee.id,
        salaryType: form.salaryType,
        baseAmount: Number(form.baseAmount || 0),
        effectiveFrom: form.effectiveFrom,
        reason: form.reason,
        components: form.componentName ? [{ name: form.componentName, kind: form.componentKind, category: form.componentKind === "Deduction" ? "Deduction" : "Allowance", amount: Number(form.componentAmount || 0), isVariable: false }] : [],
      });
      toast.success("تم حفظ نسخة جديدة من هيكل الراتب");
      queryClient.invalidateQueries({ queryKey: ["hrEmployee", String(employee.id)] });
    } catch (error) { toast.error(error.message); }
  };
  return (
    <div className="grid gap-3">
      {employee.salary && <div className="rounded-2xl border bg-white p-4"><p className="font-bold">{labelOf(employee.salary.salaryType)} · {money(employee.salary.baseAmount)}</p><p className="text-sm text-slate-500">ساري من {day(employee.salary.effectiveFrom)}</p></div>}
      {(employee.salaryHistory || []).map((row) => <p key={row.id} className="text-sm">نسخة {row.version}: {labelOf(row.salaryType)} {money(row.baseAmount)} من {day(row.effectiveFrom)} {row.reason ? `· ${row.reason}` : ""}</p>)}
      {canEdit && (
        <form onSubmit={save} className="grid gap-2 rounded-2xl border bg-white p-4">
          <Field label="نوع الراتب">
            <select className={inputClass} value={form.salaryType} onChange={(event) => setForm({ ...form, salaryType: event.target.value })}>
              {["Monthly", "Daily", "Hourly", "Piece", "Custom"].map((type) => <option key={type} value={type}>{labelOf(type)}</option>)}
            </select>
          </Field>
          <Field label="القيمة الأساسية"><input className={inputClass} type="number" step="0.01" value={form.baseAmount} onChange={(event) => setForm({ ...form, baseAmount: event.target.value })} /></Field>
          <Field label="تاريخ السريان"><input className={inputClass} type="date" required value={form.effectiveFrom} onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })} /></Field>
          <Field label="السبب"><input className={inputClass} required value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} /></Field>
          <Field label="مكون إضافي"><input className={inputClass} value={form.componentName} onChange={(event) => setForm({ ...form, componentName: event.target.value })} placeholder="بدل أو خصم" /></Field>
          <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white">حفظ هيكل جديد</button>
        </form>
      )}
    </div>
  );
}

function SimpleList({ queryKey, queryFn, render }) {
  const query = useQuery({ queryKey, queryFn });
  if (query.error) return <p className="text-sm text-rose-600">{query.error.message}</p>;
  const rows = query.data || [];
  if (!query.isLoading && rows.length === 0) return <Empty text="لا توجد سجلات." />;
  return <div className="grid gap-2">{rows.map((row) => <div key={row.id} className="flex items-center justify-between rounded-2xl border bg-white p-3 text-sm"><span>{render(row)}</span><StatusPill value={row.status} /></div>)}</div>;
}
